// Training-style guide for the FMS Admin Panel UI, written for both technical
// and non-technical users. Heavy on tables/examples/callouts rather than long
// prose, per the requested "as visual as possible" training style.
export const FMS_UI_TRAINING_GUIDE_MARKDOWN: string = `# FMS Admin Panel — How to Use This UI

> **Who this is for:** Anyone using the FMS Admin Panel — no coding background required. If you can use a spreadsheet, you can use this guide.
>
> **How to read it:** Skim the tables first. Each section has a "Where to click" table and a worked example before any explanation.

---

## 1. The 60-Second Map of the App

\`\`\`
 Left Sidebar (always visible)          Top Bar (on list pages)
 ────────────────────────────           ─────────────────────────
 🏠 Home                                 🎚️ Filter icon  → opens the filter popup
 📊 Dashboard        ▸ 7 sub-pages       ⬇️  Column headers → sort a column
 🧳 Trip Ledger      ▸ trips
 📄 Invoices         ▸ invoices
 💳 Payments         ▸ money in/out
 🛠️ Finance Resources ▸ VCCs, alerts
 👥 TST Resources
 📚 Documentation    ▸ you are here
\`\`\`

Every page in FMS follows the same skeleton: **a list (grid) → a filter → a detail view**. Once you learn that pattern once, you know it everywhere.

---

## 2. Roles — What You Can See Depends on Your Role

FMS shows or hides entire menu sections based on the role attached to your login (your Azure AD account). You don't choose your role — an administrator assigns it. If a menu item you expect is missing, it's almost always a role issue, not a bug.

| Role | Plain-English meaning | Typical access |
|---|---|---|
| **User** | Base-level staff account | Home, Dashboard/Transactions, general read access |
| **Manager** | Team lead / operations | Everything **User** has, plus Invoices, Payments, Finance Resources, Trip Ledger management |
| **Finance** | Finance/accounting staff | Same broad access as Manager, focused on invoices, payments, VCC matching, commission data |
| **Director** | Senior leadership | Strategic/aggregate views — e.g. profitability-with-agency reports — plus everything Finance sees |
| **Admin** | System administrator | Unrestricted — every page, every environment |

> 💡 **How it actually works under the hood:** every page has a short list of roles allowed to open it. When you click a link, FMS checks your login token against that list. Match → page opens. No match → you land on a **"No Access"** page. This is why two people can look at the exact same sidebar and see different numbers of options.

---

## 3. Trip Ledger — What "Trips" Mean

A **Trip** is the umbrella record that groups everything booked for one traveler's journey — flights, hotels, cars — under one roof, even if that journey involves multiple invoices from different vendors.

\`\`\`
            TRIP  "Smith Family — Cancun, Aug 2026"
             │
   ┌─────────┼──────────┬───────────────┐
   ▼         ▼          ▼               ▼
 Flight    Hotel      Invoice #1      Invoice #2
 booking   booking    (airline)       (hotel)
\`\`\`

**Where to click**

| Step | Action |
|---|---|
| 1 | Sidebar → **Trip Ledger** |
| 2 | Grid loads showing every trip (Trip Name, Record Locator, Start/End dates, Status) |
| 3 | Click the 🎚️ filter icon (top-right of the grid) to narrow by Trip Name, Record Locator, Status, date range, Cancelled/Archived, or "With Invoice" |
| 4 | Click a **Trip Number** to open its full detail as a **popup right on this page** |

**Example:** Searching Record Locator \`ABC123\` in the filter instantly narrows the grid to just that itinerary, instead of scrolling through every trip in the system.

**Trip detail page shows (scroll down, all on one page — not separate tabs):**
- Trip header info (name, dates, itinerary reference)
- Linked **Invoices** list
- Linked **Payments** grid
- A **Generate PDF** button (if your role allows it) to export the trip

---

## 4. Filters — Where They Live on Every Page

> 💡 **Rule of thumb:** on almost every grid/list page in FMS, the filter is the **🎚️ funnel/stacked-bars icon in the top-right of the toolbar above the grid** (Material's "filter_list" icon — three horizontal bars, not a magnifying glass). Click it → a popup window appears with fields relevant to that page → fill in what you know → **Apply**. Click **Reset** to clear it again.

| Page | Filter icon location | You can filter by |
|---|---|---|
| Trip Ledger | Top-right toolbar | Trip Name, Record Locator, Status, dates, Cancelled/Archived, Has Invoice |
| Invoices (Fms Invoices) | Top-right toolbar | Invoice Number, Client Name, Issue Date range, Type Code, Data Source |
| Payments → Payments Reconciliation | Top-right toolbar | File Name, Client Name, dates, Reconciliation status |
| Dashboard → Finance | Top-right toolbar | Date range, Travel Type, Submit-To, Data Source, Branch |
| Dashboard → any ERP sync panel | Top-right toolbar | Date range + sync/push Status (Pending/Success/Failed) |

**Example:** On the Invoices grid, click 🎚️ → set Issue Date range to last 7 days → Apply. The grid instantly re-loads to only those rows — no page reload needed.

---

## 5. Opening a Record — Popup vs. New Tab

Every clickable ID/number in FMS is a real hyperlink, so **your click** decides how it opens:

| To open it as... | Do this |
|---|---|
| A **popup** on top of the current page (default) | **Left-click** the blue underlined ID/number (Trip Number, Invoice Number, Booking Number) — a dialog opens right there; close it to return to the exact same grid/scroll position |
| A **new browser tab**, so you can keep the list open and compare records side by side | **Right-click** the blue ID/number → choose **"Open link in new tab"** from the browser's context menu |
| A **popup** for a quick look/edit on a specific row | Click the row's action icon (eye 👁️, pencil ✏️, info ⓘ) |
| A **popup** for filtering | Click the 🎚️ filter icon |

**Example:** On the Trip Ledger grid, a normal left-click on Trip Number \`TRP-000123\` pops up that trip's detail over the current page. If you instead want it in its own tab (to compare two trips at once), right-click \`TRP-000123\` and choose "Open link in new tab."

---

## 6. How to Check an Invoice — Step by Step

\`\`\`
Sidebar → Invoices → grid loads
   │
   ├─ 🎚️ Filter by Invoice Number / Client / Date range
   │
   ▼
Click the Invoice Number (blue link) → left-click for a popup, or right-click → "Open link in new tab" for its own tab
   │
   ▼
Invoice detail shows: header (client, branch, issue date, type),
line items / bookings on the invoice, payment status, linked trip
\`\`\`

| What you're checking | Where to find it on the invoice |
|---|---|
| Who the invoice belongs to | Header section — Client Name / Central Document ID |
| Is it paid? | **PayStatus** field on the header |
| What it's for | Booking/line-item list below the header (vendor, fare, commission) |
| Which trip it belongs to | "Trip" reference/link in the header |
| Refund vs. normal sale | **Invoice Type** — Sale, Refund, Adjustment, Spoiled, Voucher |

> 💡 **Tip:** if \`InvoiceType\` shows anything other than "Sale," treat the amounts as a correction, not new revenue.

---

## 7. Booking Details — General Info, Agents, UDID, Segments

Open any booking (from a trip, or from the ADX Bookings grid) and you'll see it laid out in sections/tabs. Here's what each one means:

| Section | Plain-English meaning | Example |
|---|---|---|
| **General Info** | The basics: vendor, service type (Air/Hotel/Car/etc.), submit-to, dates, fare | "Air booking with United, departs 2026-08-15, total fare $450" |
| **Agents** | The travel agent(s) tied to this booking and their commission split | Agent code \`AG1234\`, 10% commission |
| **UDID** | Small tagged pieces of extra reference data — think of them as labeled sticky notes attached to the booking | UDID key **227** = the booking's unique dedupe ID (prevents the same booking being loaded twice); UDID key **911** = a backup record locator used if the main one is missing |
| **Segments** | Each individual flight leg (only shown for Air bookings) | Segment 1: \`YYZ → MIA\`, flight UA1234, Economy; Segment 2: \`MIA → LAX\`, flight UA5678, Business |
| **Hotel / Support Fee / Markup** | Extra detail panels that only appear when relevant to that booking type | Hotel property name & confirmation number |

**Worked example — reading a 2-segment flight:**

| Segment | Route | Flight | Class | Departs |
|---|---|---|---|---|
| 1 | Toronto (YYZ) → Miami (MIA) | UA1234 | Economy (Y) | 2026-08-15 09:30 |
| 2 | Miami (MIA) → Los Angeles (LAX) | UA5678 | Business (J) | 2026-08-15 17:45 |

This is one booking with **one record locator**, but **two segments** because the traveler changes planes in Miami.

> 💡 **Why UDIDs matter to you:** if you ever see a booking that looks like a duplicate, it's the UDID 227 value the system used behind the scenes to decide "same booking" vs. "new booking" — you don't need to enter it yourself, it just explains why FMS didn't create a duplicate.

---

## 8. Dashboards — What Each One Is For

The **Dashboard** menu has several sub-pages. Think of each as a different lens on the same data:

| Dashboard | What it shows | Use it when you want to know... |
|---|---|---|
| **Transactions** | Support ticket metrics: assigned/closed tickets, active cases, average handle time | "How is the support team doing this week?" |
| **VCC** | Virtual Credit Card funding date ranges and release dates | "When do the current VCC funds get released?" |
| **Finance** | Gross fare & commission broken down by Travel Type, External Travel Type, and Submit-To, with an Amount/Percentage toggle | "Which product line (Air, Hotel, Car…) is driving revenue this month?" |
| **Admin Panel** | Total invoices processed vs. failed, system error logs, App Insights request/exception charts | "Is anything broken in the pipeline right now?" |
| **ADX-FMS-ERP Panel** | Sync status of ADX invoices pushed to the D365 ERP system (pending/success/failed counts) | "Did today's invoices make it into the finance system?" |
| **TMT-FMS-ERP Panel** | Same idea as above, but for the TMT source system | "Did TMT invoices reach D365?" |
| **ERP Outbound Report** | Every outbound message sent to ERP, grouped or as a flat list, with status | "Show me every message FMS tried to send to ERP today, and which ones failed" |
| **ADX Service Bus Monitor** | Compares invoice counts coming through the Service Bus feed vs. the HTTP feed, flags MATCH/MISMATCH | "Are the two ADX invoice delivery channels in agreement?" |

**Example:** If Finance says "an invoice never showed up in D365," start at **ADX-FMS-ERP Panel** (or **TMT-FMS-ERP Panel** depending on source), filter by date, and look for that invoice in the Failed list before escalating.

---

## 9. How Agents, Clients & Vendors Get Into FMS (Sync, Today)

FMS doesn't create these records by hand — they're synced in automatically from the systems that already manage them, on a schedule, using Azure Data Factory pipelines:

\`\`\`
   TRAMS (legacy system)        ADX (current booking/quote system)
        │                               │
        │  daily / hourly sync jobs     │  daily / every 15 min sync jobs
        ▼                               ▼
              FMS "DBOv2" database
         (Agent, Client, Vendor, Trip, Booking,
          Invoice, Payment, UDID, Segment tables)
                     │
                     ▼
            FMS Admin Panel (what you see)
\`\`\`

| Data | Comes from | Roughly how often |
|---|---|---|
| **Agents** | Both TRAMS and ADX | Daily (mid-morning) |
| **Clients** | ADX | Daily (mid-morning) |
| **Vendors** | Both TRAMS and ADX | Daily (mid-morning) |
| **Bookings** | TRAMS | About every hour |
| **Booking UDIDs / Agents on a booking** | TRAMS | About every 30 minutes |
| **Invoices** | TRAMS | About every 90 minutes |
| **Virtual Credit Cards (VCC)** | ADX | About every 15 minutes |
| **Currencies / Countries** | ADX | Monthly |

> 💡 **What "sync" means in practice:** these jobs only pull what **changed** since the last run (a "delta load"), not the whole table every time — so a change made in TRAMS or ADX typically shows up in FMS within the interval listed above, not instantly.

**Where it's stored once it lands:** the FMS "DBOv2" database keeps one table per concept — \`Agent\`, \`Client\`, \`Vendor\`, \`Trip\`, \`Booking\` / \`AdxBooking\`, \`Invoice\` / \`AdxInvoice\`, \`BookingUdid\`, \`BookingSegment\`, and payment tables — plus a couple of calculated **views** that add up numbers on the fly:

| View | What it calculates |
|---|---|
| **Client Balance** view | For each client: total billed − total paid = what the client still owes |
| **Vendor Balance** view | For each vendor: total billed − total paid = what FMS still owes that vendor |

Nothing needs to be manually re-typed into FMS for Agents/Clients/Vendors — if a record looks wrong, the fix belongs in the *source* system (TRAMS or ADX), and it will flow through on the next scheduled sync.

---

## 10. Quick Reference Cheat-Sheet

| I want to... | Go here |
|---|---|
| See a client's or a trip's bookings | **Trip Ledger** → filter by Record Locator or Trip Name |
| Check if an invoice was paid | **Invoices** → open the invoice → PayStatus field |
| See why a booking wasn't duplicated | Booking detail → **UDID** tab → key 227 |
| See each flight leg of a trip | Booking detail → **Segments** tab (Air bookings only) |
| Find who gets commission on a booking | Booking detail → **Agents** tab |
| Check if today's invoices reached D365 | **Dashboard → ADX-FMS-ERP Panel** (or TMT panel) |
| Narrow any grid down | Click the 🎚️ icon, top-right of the grid |
| Compare two records side-by-side | Right-click each blue ID link → "Open link in new tab" (a plain left-click opens a popup instead) |

---

*This guide describes the UI as currently configured. Some menu items may be temporarily hidden for your account or environment — if something described here isn't visible, check with an administrator before assuming it's missing.*
`;
